import React, { useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, HelpCircle, MessageSquare, ChevronDown, Search, Lightbulb, ExternalLink } from 'lucide-react';
import Footer from '../../components/landing/Footer';

const HelpCenterMediaEvents = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleBack = () => {
    window.history.back();
  };

  const [openIndex, setOpenIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const faqs = [
    {
      question: "How can my brand or organisation partner with TripSecure Media & Events?",
      answer: "You can partner with us through event sponsorships, media collaborations, branded content, or campaign activations. Simply contact our partnerships team via the Contact Us page.",
      category: "partnerships"
    },
    {
      question: "Can I exhibit or sponsor the TripSecure Expo?",
      answer: "Yes. The TripSecure Expo is open to insurers, transport operators, travel companies, safety brands, fintechs, NGOs, and development partners. Sponsorship and exhibition packages are available.",
      category: "expo"
    },
    {
      question: "Do you produce custom media content for organisations?",
      answer: "Yes. We create tailored podcasts, videos, documentaries, and digital campaigns aligned with your brand objectives.",
      category: "media"
    },
    {
      question: "How can I attend or participate in TripSecure events?",
      answer: "Event registration details are published on our website and social media platforms ahead of each event.",
      category: "events"
    },
    {
      question: "Can individuals participate as speakers or content contributors?",
      answer: "Yes. Subject-matter experts, professionals, and creators can apply to speak or collaborate on TripSecure media projects.",
      category: "participation"
    }
  ];

  const filteredFaqs = faqs.filter(faq => 
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-gradient-to-br via-white to-orange-50 from-slate-50">
      {/* Back Button */}
      <div className="fixed left-4 top-24 z-50">
        <motion.button
          onClick={handleBack}
          whileHover={{ scale: 1.05, x: -5 }}
          whileTap={{ scale: 0.95 }}
          className="flex gap-2 items-center px-4 py-2.5 rounded-xl border border-gray-200 shadow-lg backdrop-blur-md transition-all duration-300 bg-white/95 hover:bg-white hover:shadow-2xl"
        >
          <ArrowLeft className="w-4 h-4 text-gray-600" />
          <span className="text-sm font-semibold text-gray-700">Back</span>
        </motion.button>
      </div>

      {/* Hero Section with Background */}
      <section className="pt-20 md:pt-24 lg:pt-28 pb-20 md:pb-24 lg:pb-28 overflow-hidden relative h-[500px] mt-16 md:mt-20">
        {/* Background Image */}
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=2070&auto=format&fit=crop"
            alt="Team Collaboration"
            className="object-cover w-full h-full"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-orange-900/85 via-blue-900/80 to-purple-900/85" />
        </div>

        {/* Floating elements */}
        <div className="absolute inset-0">
          {[...Array(15)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-1 h-1 bg-white rounded-full"
              animate={{
                y: [0, -100, 0],
                opacity: [0, 1, 0],
              }}
              transition={{
                duration: Math.random() * 3 + 2,
                repeat: Infinity,
                delay: Math.random() * 2,
              }}
              style={{
                left: `${Math.random() * 100}%`,
                top: `${Math.random() * 100}%`,
              }}
            />
          ))}
        </div>
        
        <div className="flex relative z-10 justify-center items-center px-6 py-12 mx-auto max-w-6xl h-full sm:px-8 lg:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full text-center"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex gap-2 items-center px-6 py-3 mb-8 rounded-full border shadow-xl backdrop-blur-md md:mb-10 bg-white/20 border-white/30"
            >
              <HelpCircle className="w-5 h-5 text-orange-300" />
              <span className="text-sm font-bold tracking-wide text-white uppercase">Help Center – Media & Events</span>
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="mb-8 text-5xl font-black text-white md:mb-10 md:text-6xl lg:text-7xl"
            >
              How Can We Help You?
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="mx-auto mb-12 max-w-2xl text-xl text-gray-100 md:mb-14"
            >
              Welcome to the TripSecure Media & Events Help Center. Here you'll find answers to common questions about our media productions, events, partnerships, and participation opportunities.
            </motion.p>

            {/* Search Bar */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="mx-auto mt-6 max-w-2xl"
            >
              <div className="relative">
                <Search className="absolute left-5 top-1/2 w-5 h-5 text-gray-400 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Search for answers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="px-14 py-5 w-full text-lg bg-white rounded-2xl border-2 border-transparent shadow-2xl transition-all focus:outline-none focus:border-orange-500 focus:ring-4 focus:ring-orange-500/20"
                />
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="px-4 -mt-16 sm:px-6">
        <div className="mx-auto max-w-5xl">
          <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
            {[
              { number: "5", label: "Common Questions", icon: Lightbulb },
              { number: "24/7", label: "Support Available", icon: MessageSquare },
              { number: "100%", label: "Response Rate", icon: HelpCircle },
            ].map((stat, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: idx * 0.1 }}
                className="relative p-6 text-center bg-white rounded-2xl border border-gray-100 shadow-lg"
              >
                <div className="inline-flex justify-center items-center mx-auto mb-3 w-14 h-14 bg-gradient-to-br from-orange-500 to-blue-600 rounded-xl shadow-lg">
                  <stat.icon className="w-7 h-7 text-white" />
                </div>
                <div className="text-4xl font-black text-gray-900">{stat.number}</div>
                <div className="mt-1 text-sm font-medium text-gray-600">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 py-20 mx-auto max-w-4xl sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 text-center"
        >
          <h2 className="mb-4 text-4xl font-black text-gray-900">Frequently Asked Questions</h2>
          <div className="mx-auto w-24 h-1.5 bg-gradient-to-r from-orange-500 to-blue-600 rounded-full" />
        </motion.div>

        {filteredFaqs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="p-12 text-center bg-white rounded-2xl border border-gray-200 shadow-lg"
          >
            <Search className="mx-auto mb-4 w-16 h-16 text-gray-300" />
            <p className="text-xl text-gray-500">No results found. Try a different search term.</p>
          </motion.div>
        ) : (
          <div className="space-y-4">
            {filteredFaqs.map((faq, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="overflow-hidden bg-white rounded-2xl border border-gray-200 shadow-md transition-all hover:shadow-xl"
              >
                <button
                  onClick={() => setOpenIndex(openIndex === index ? null : index)}
                  className="flex gap-4 justify-between items-center p-6 w-full text-left transition-colors hover:bg-gray-50"
                >
                  <div className="flex flex-1 gap-4 items-start">
                    <div className="flex flex-shrink-0 justify-center items-center w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl shadow-lg">
                      <span className="text-sm font-bold text-white">{index + 1}</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold leading-tight text-gray-900">
                        {faq.question}
                      </h3>
                    </div>
                  </div>
                  <motion.div
                    animate={{ rotate: openIndex === index ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="flex-shrink-0"
                  >
                    <ChevronDown className="w-6 h-6 text-gray-400" />
                  </motion.div>
                </button>
                
                <AnimatePresence>
                  {openIndex === index && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3 }}
                      className="overflow-hidden"
                    >
                      <div className="px-6 pb-6 ml-14">
                        <p className="text-base leading-relaxed text-gray-700">
                          {faq.answer}
                        </p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Contact CTA */}
      <section className="px-4 py-20">
        <div className="mx-auto max-w-4xl sm:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="overflow-hidden relative p-12 text-center bg-gradient-to-r from-orange-600 to-blue-600 rounded-3xl shadow-2xl"
          >
            {/* Decorative elements */}
            <div className="overflow-hidden absolute inset-0">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 30, repeat: Infinity, ease: "linear" }}
                className="absolute -top-24 -right-24 w-64 h-64 bg-white rounded-full opacity-10"
              />
              <motion.div
                animate={{ rotate: -360 }}
                transition={{ duration: 35, repeat: Infinity, ease: "linear" }}
                className="absolute -bottom-24 -left-24 w-64 h-64 bg-white rounded-full opacity-10"
              />
            </div>

            <div className="relative z-10">
              <MessageSquare className="mx-auto mb-6 w-16 h-16 text-white" />
              <h3 className="mb-4 text-3xl font-black text-white md:text-4xl">Still Have Questions?</h3>
              <p className="mx-auto mb-8 max-w-2xl text-lg text-white/90">
                If your question is not listed here, please reach out to us directly.
              </p>
              <motion.a
                href="/contact-us-media-events"
                whileHover={{ scale: 1.05, boxShadow: "0 20px 40px rgba(0,0,0,0.3)" }}
                whileTap={{ scale: 0.95 }}
                className="inline-flex gap-2 items-center px-8 py-4 text-lg font-bold text-orange-600 bg-white rounded-xl shadow-xl transition-all hover:bg-gray-50"
              >
                Contact Us
                <ExternalLink className="w-5 h-5" />
              </motion.a>
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HelpCenterMediaEvents;
